import {
    Viewer,
    Ion,
    GeoJsonDataSource,
    OpenStreetMapImageryProvider,
    ImageryLayer,
    BingMapsImageryProvider,
    WebMapServiceImageryProvider,
    TileMapServiceImageryProvider,
    ArcGisMapServerImageryProvider,
    BingMapsStyle,
    Cesium3DTileset,
    Rectangle,
    Camera,
    defined,
    JulianDate,
    NearFarScalar,
    Cesium3DTileStyle,
    Cartographic,
    Cartesian3,
    Color

} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import './viewer.css'
import { arcgisBaseURL } from '../../utils/layers.js'
import { DateFormat } from '../../utils/dateFormat.js'
import { WMS, Tileset, TMS, GeoJSON, ArcgisMapserver, Terrain, PointCloud } from '../../utils/layer-types.js'

export let viewer;
let _selectedLayer;
let _changeLayer, _findLayer;
let _selectedFeature, _setSelectedFeature;
let _layerDescriptions;
let _setZoomLevel;
let _prevSelectedEntity;
export function setReactStateFunctions(findLayerFunc, changeLayer) {
    _findLayer = findLayerFunc;
    _changeLayer = changeLayer;
}
export default async function InitializeViewer(layers, selectedFeature, setSelectedFeature, terrainProvider, setZoomLevel) {
    _setSelectedFeature = setSelectedFeature;
    _selectedFeature = selectedFeature;
    _setZoomLevel = setZoomLevel;
    viewer = new Viewer('cesiumContainer', {
        terrainProvider: await terrainProvider.init(),
        baseLayer: new ImageryLayer(new OpenStreetMapImageryProvider({
            url: "https://tile.openstreetmap.org/"
        })),
        imageryProvider: new BingMapsImageryProvider({
            url: 'https://dev.virtualearth.net',
            key: '',
            mapStyle: BingMapsStyle.AERIAL_WITH_LABELS
        }),
        baseLayerPicker: false,
        selectionIndicator: false,
        infoBox: false,
        navigationHelpButton: false,
        sceneModePicker: false,
        homeButton: false
    });
    viewer.animation.container.style.visibility = 'hidden';
    viewer.timeline.container.style.visibility = 'hidden';
    viewer._cesiumWidget._creditContainer.style.display = "none";

    var extent = Rectangle.fromDegrees(57.9124, 23.5748, 58.1265, 23.6460);
    Camera.DEFAULT_VIEW_RECTANGLE = extent;
    Camera.DEFAULT_VIEW_FACTOR = 0;

    loadLayersDescription();
    viewer.selectedEntityChanged.addEventListener(selectEntity);
    viewer.camera.changed.addEventListener(getLevel);

    layers.forEach(e => addLayers(e));
}

function loadGeoJSONLayer(layer) {
    let { data, onLoad, ...options } = layer.options;
    options.name = layer.name;
    options.show = layer.visible === true;
    GeoJsonDataSource.load(
        data,
        options
    ).then(d => {
        let ds = viewer.dataSources.getByName(layer.name);
        if (_findLayer((l) => l.id == layer.id).visible === true) {
            if (ds.length === 0) {
                d.name = layer.name;
                d.show = layer.visible === true;
                viewer.dataSources.add(d);
                if (onLoad)
                    onLoad(d);
            }
            else
                ds.slice(1).forEach(e => viewer.dataSources.remove(e));
        } else {
            ds.forEach(e => viewer.dataSources.remove(e));
        }
    })
        .catch((error) => {
            console.log('Error loading geojson: ' + error);
        }).finally(() => {
            _changeLayer({ ..._findLayer((l) => l.id == layer.id), loading: false });
        });
}

async function loadLayersDescription() {
    if (arcgisBaseURL) {
        let url = arcgisBaseURL + '/arcgis/rest/services/Sur1/Sur/FeatureServer/layers?f=pjson';
        fetch(url).then(r => r.json()).then(data => { _layerDescriptions = data; })
            .catch(e => console.log("Error loading layers description " + e));
    }
    else {
        _layerDescriptions = { layers: [] };
    }
}

function addLayers(layer, layersDescription) {
    if (layer.type === GeoJSON && layer.visible === true) {
        loadGeoJSONLayer(layer, layersDescription);
    }
    else if (layer.type === Tileset || layer.type === PointCloud) {
        let { data, onLoad, ...options } = layer.options;
        options.name = layer.name;
        options.show = layer.visible;
        Cesium3DTileset.fromUrl(
            data,
            options
        ).then((d) => {
            if (layer.type === PointCloud){
                d.pointCloudShading.attenuation = true;
                if (options.style) {
                    d.style = new Cesium3DTileStyle(options.style);
                }
            }
            else {
                let alpha = layer.opacity;
                d.style = new Cesium3DTileStyle({
                    color:
                        "undefined === ${COLOR}.x ? color('white', " +
                        alpha +
                        " ) : rgba(${COLOR}.r * 255, ${COLOR}.g * 255, ${COLOR}.b *255, " +
                        alpha +
                        ")",
                });
            }
            viewer.scene.primitives.add(d);
            if (onLoad)
                onLoad(d);

            navigator.locks.request("add_layer_datasource", () => _changeLayer({ ..._findLayer((l) => l.id == layer.id), dataSource: d }));
            d.show = layer.visible === true;
            viewer.flyTo(d);
        })
            .catch((error) => {
                console.log('Error loading tileset: ' + error);
            });
    }
    else if (layer.type == TMS) {
        let { data, onLoad, ...options } = layer.options;
        options.name = layer.name;
        options.show = layer.visible;

        var imageryLayer = ImageryLayer.fromProviderAsync(
            TileMapServiceImageryProvider.fromUrl(
                data,
                options)
        );
        imageryLayer.readyEvent.addEventListener(error => {
            navigator.locks.request("add_layer_datasource", () => _changeLayer({ ..._findLayer((l) => l.id == layer.id), dataSource: imageryLayer }));
            imageryLayer.show = layer.visible == true;
            imageryLayer.alpha = 1.0;
            viewer.imageryLayers.add(imageryLayer);
        });
        imageryLayer.errorEvent.addEventListener(error => {
            console.log(`There was an error creating the tms layer. ${error}`);
        });
    }
    else if (layer.type == WMS) {
        let { onLoad, ...options } = layer.options;
        options.name = layer.name;
        options.show = layer.visible;

        var imageryLayer = ImageryLayer.fromProviderAsync(
            new WebMapServiceImageryProvider(options)
        );
        imageryLayer.readyEvent.addEventListener(error => {
            navigator.locks.request("add_layer_datasource", () => _changeLayer({ ..._findLayer((l) => l.id == layer.id), dataSource: imageryLayer }));
            imageryLayer.show = layer.visible == true;
            viewer.imageryLayers.add(imageryLayer);
        });
        imageryLayer.errorEvent.addEventListener(error => {
            console.log(`There was an error creating the wms layer. ${error}`);
        });
    }
    else if (layer.type == ArcgisMapserver) {
        let { data, onLoad, ...options } = layer.options;
        options.name = layer.name;
        options.show = layer.visible;

        var argisLayer = ImageryLayer.fromProviderAsync(
            ArcGisMapServerImageryProvider.fromUrl(
                data,
                options)
        );
        argisLayer.readyEvent.addEventListener(() => {
            navigator.locks.request("add_layer_datasource", () => _changeLayer({ ..._findLayer((l) => l.id == layer.id), dataSource: argisLayer }));
            argisLayer.show = layer.visible == true;
            viewer.imageryLayers.add(argisLayer);
        });
        argisLayer.errorEvent.addEventListener(error => {
            console.log(`There was an error creating the imagery layer. ${error}`);
        });
    }

    if (layer.elements) {
        layer.elements.forEach(e => addLayers(e));
    }
}

export function updateLayerOpacity(layer) {
    if (layer.type === Tileset && layer.dataSource) {
        layer = _findLayer((l) => l.id == layer.id);
        let alpha = layer.opacity;
        layer.dataSource.style = new Cesium3DTileStyle({
            color:
                "undefined === ${COLOR}.x ? color('white', " +
                alpha +
                " ) : rgba(${COLOR}.r * 255, ${COLOR}.g * 255, ${COLOR}.b *255, " +
                alpha +
                ")",
        });
    }
    if (layer.type === TMS && layer.dataSource) {
        layer = _findLayer((l) => l.id == layer.id);
        layer.dataSource.alpha = layer.opacity;
    }
}

export function updateLayerVisible(layer) {
    layer = _findLayer((l) => l.id == layer.id);
    if (layer.type === GeoJSON) {
        if (layer.visible === true)
            loadGeoJSONLayer(layer);
        else {
            viewer.dataSources.getByName(layer.name).forEach(d => viewer.dataSources.remove(d));
            viewer.dataSources.remove(layer.dataSource);
            let newLayer = { ..._findLayer((l) => l.id == layer.id) };
            newLayer.dataSource = undefined;
            newLayer.loading = false;

            _changeLayer(newLayer);
        }
    }
    else {
        if (layer.dataSource !== undefined) {
            layer.dataSource.show = layer.visible === true;
        }
        _changeLayer({ ..._findLayer((l) => l.id == layer.id), loading: false });
    }
}

export function unselectFeature() {
    _setSelectedFeature({ ..._selectedFeature, visible: false });
    if (_selectedLayer) {
        _changeLayer({ ..._findLayer((l) => l.id == _selectedLayer.id), selected: false });
        _selectedLayer = undefined;
    }
}
function updateEntityColor(selectedEntity, color) {
    let oldColor;
    if (selectedEntity.model) {
        oldColor = selectedEntity.model.color;
        selectedEntity.model.color = color;
    }
    else if (selectedEntity.polylineVolume) {
        oldColor = selectedEntity.polylineVolume.material.color;
        selectedEntity.polylineVolume.material.color = color;
        selectedEntity.polyline.material.color = color;
    }
    else if (selectedEntity.box) {
        oldColor = selectedEntity.box.material.color;
        selectedEntity.box.material.color = color;
    }
    else if (selectedEntity.cylinder) {
        oldColor = selectedEntity.cylinder.material.color;
        selectedEntity.cylinder.material.color = color;
    }
    else if (selectedEntity.polygon) {
        oldColor = selectedEntity.polygon.material.color;
        selectedEntity.polygon.material.color = color;
    }  
    else if (selectedEntity.billboard) {
        oldColor = selectedEntity.billboard.color;
        selectedEntity.billboard.color = color;
    }
    return oldColor;
}
function coloredEntity(selectedEntity) {
    //если есть предыдущий выбранный элемент, то возвращаем у него цвет в исходный
    if (defined(_prevSelectedEntity)){
        updateEntityColor(_prevSelectedEntity.entity, _prevSelectedEntity.color);    
    }
    //если выделили объект, то запоминаем исходный цвет и окрашиваем в Color.RED
    if (defined(selectedEntity)) {        
        let oldColor = updateEntityColor(selectedEntity, Color.RED);
        _prevSelectedEntity = {entity: selectedEntity, color: oldColor};
    }
    else _prevSelectedEntity = null;
}

function selectEntity(selectedEntity) {
    //Снимаем окраску с предыдущего и окрашиваем выделенный    
    coloredEntity(selectedEntity);
    if (defined(selectedEntity) && selectedEntity.properties) {
        let layer = _findLayer((layer) => layer.name === selectedEntity.entityCollection.owner.name);
        if (layer === undefined)
            return unselectFeature();
        // selectedEntity._name = layer.name;
        if (_selectedLayer && _selectedLayer !== layer) {
            _changeLayer({ ..._findLayer((l) => l.id == _selectedLayer.id), selected: false });
        }
        _changeLayer({ ..._findLayer((l) => l.id == layer.id), selected: true });
        _selectedLayer = layer;
        let featureDescription = selectedEntity.properties.getValue(new JulianDate());
        let description = {};

        //Получаем слой из описания
        let layerDesc = _layerDescriptions.layers.find(element => element.name == layer.name);

        if (layerDesc)
            layerDesc.fields.forEach((field) => {
                if (field.domain !== null) {
                    let domainValue = field.domain.codedValues.find(v => v.code === featureDescription[field.name]);
                    description[field.name] = domainValue ? domainValue.name : featureDescription[field.name];
                }
                else if (field.type === "esriFieldTypeDate")
                    description[field.name] = DateFormat.format.date(new Date(featureDescription[field.name]), 'MM-dd-yyyy');
                else
                    description[field.name] = featureDescription[field.name];
            });
        else
            description = featureDescription;
        _setSelectedFeature({ visible: true, feature: description, layer: _selectedLayer });
    } else {
        unselectFeature();
    }
}

export function focusOnSelectedEntity() {
    if (defined(viewer.selectedEntity))
        viewer.flyTo(viewer.selectedEntity);
}

export function focusOnHome() {
    viewer.camera.flyTo({ destination: Camera.DEFAULT_VIEW_RECTANGLE });
}

export async function setTerrainProvider(t) {
    viewer.terrainProvider = await t.init();
}

function getLevel() {
    let zoomLevelHeights = getZoomLevelHeights(1);
    let cameraHeight = viewer.camera.positionCartographic.height;
    let distance = Number.MAX_VALUE, cd;
    let level = 1;
    for (var i = 0; i < zoomLevelHeights.length; i++) {
        cd = Math.abs(zoomLevelHeights[i].height - cameraHeight);
        if (cd < distance) {
            distance = cd;
            level = zoomLevelHeights[i].level;
        }
    }
    _setZoomLevel(level)
}

function getZoomLevelHeights(precision) {
    precision = precision || 10;

    var step = 100000.0;

    var result = [];
    var currentZoomLevel = 0;
    for (var height = 100000000.0; height > step; height = height - step) {
        var level = detectZoomLevel(height);
        if (level === null) {
            break;
        }

        if (level !== currentZoomLevel) {
            var minHeight = height;
            var maxHeight = height + step;
            while (maxHeight - minHeight > precision) {
                height = minHeight + (maxHeight - minHeight) / 2;
                if (detectZoomLevel(height) === level) {
                    minHeight = height;
                }
                else {
                    maxHeight = height;
                }
            }

            result.push({
                level: level,
                height: Math.round(height)
            });

            currentZoomLevel = level;

            if (result.length >= 2) {
                step = (result[result.length - 2].height - height) / 1000.0;
            }
        }
    }

    return result;
}

function detectZoomLevel(distance) {
    var scene = viewer._cesiumWidget.scene;
    var tileProvider = scene.globe._surface.tileProvider;
    var quadtree = tileProvider._quadtree;
    var drawingBufferHeight = viewer._cesiumWidget.canvas.height;
    var sseDenominator = viewer._cesiumWidget.camera.frustum.sseDenominator;

    for (var level = 0; level <= 25; level++) {
        var maxGeometricError = tileProvider.getLevelMaximumGeometricError(level);
        var error = (maxGeometricError * drawingBufferHeight) / (distance * sseDenominator);
        if (error < quadtree.maximumScreenSpaceError) {
            return level;
        }
    }

    return null;
}